import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useParams, useLocation, useSearchParams, useNavigate } from 'react-router-dom';
import { useBreadcrumb } from '../../contexts/BreadcrumbContext';
import billingService from '../../services/billingService';
import { toast } from 'react-toastify';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { exportColoredTablePdf } from '../../utils/coloredTablePdfExport';
import { faCalendarAlt, faHeartbeat, faFileAlt, faList, faEye } from '@fortawesome/free-solid-svg-icons';
import Heading from '../../components/Common/Heading/Heading';
import SearchBar from '../../components/Common/SearchBar/SearchBar';
import ListScreenPaginationBar from '../../components/Common/ListScreenPaginationBar/ListScreenPaginationBar';
import Table from '../../components/Common/Table/Table';
import AppDateInput from '../../components/Common/AppDateInput/AppDateInput';
import StatusBadge from '../../components/Common/StatusBadge/StatusBadge';
import EmailModal from '../../components/EmailModal/EmailModal';
import PrintModal from '../../components/PrintModal/PrintModal';
import BillingReportPreviewModal from './BillingReportPreviewModal';
import EventLogSection from '../../components/EventLogSection/EventLogSection';
import useEventLogModalScrollLock from '../../hooks/useEventLogModalScrollLock';
import { sendReportEmail } from '../../utils/reportEmailSend';
import '../../components/Common/ListScreenPageLayout/listScreenPage.css';
import '../PracticeManagement/PatientManagement/PatientProgramAnalytics.css';
import '../PracticeManagement/SystemCaregiverManagement/SystemCaregiverManagement.css';
import './ManageBillingReport.css';
import GenerateBillingModal from './GenerateBillingModal';
import { formatDateOnlyUs, normalizeDateOnly } from '../../utils/dateOnlyUtils';
import {
  DEFAULT_BILLING_PERIOD_DAYS,
  buildBillingCycleList,
  findBillingCycleKey,
  getPracticeBillingPeriod,
  getTodayYmd
} from '../../utils/billingPeriodUtils';
import { readBillingPracticeCache, writeBillingPracticeCache } from '../../utils/billingPracticeCache';
import { getBillingCptCodesForProgram } from '../../utils/patientBillingUtils';
import { getPatientStatusSortOrder } from '../../utils/patientStatusUtils';
import {
  buildPatientDetailsPath,
  resolvePatientRecordId,
  resolvePatientPracticeId
} from '../../utils/patientRouteUtils';

const MANAGE_BILLING_PROGRAM_TABS = [
  { id: 'RPM', label: 'RPM', icon: faHeartbeat, param: 'rpm' },
  { id: 'CCM', label: 'CCM', icon: faFileAlt, param: 'ccm' },
  { id: 'PCM', label: 'PCM', icon: faList, param: 'pcm' }
];

function normalizePracticePrograms(programs) {
  const list = (programs || []).map((p) => String(p).toUpperCase()).filter(Boolean);
  return list.length > 0 ? list : ['RPM'];
}

const getStatusOrder = (status) => getPatientStatusSortOrder(status);

const RPM_EMAIL_COLUMNS = [
  'Status',
  'Vitals',
  'Patient Name',
  'DOB',
  'Modal #',
  'Serial #',
  'RPM Start Date',
  'Upload Date/Time',
  'Measurement Days',
  'Service Time',
  'Caregiver',
  'Provider',
  'Eligible Cpt Codes'
];

const CCM_EMAIL_COLUMNS = [
  'Status',
  'Patient Name',
  'DOB',
  'Clinical Min',
  'Provider Min',
  'Complexity',
  'Billing Path',
  'Caregiver',
  'Provider',
  'Eligible CPT Codes'
];

const PCM_EMAIL_COLUMNS = [
  'Status',
  'Patient Name',
  'DOB',
  'Clinical Min',
  'Provider Min',
  'Service Time',
  'Caregiver',
  'Provider',
  'Eligible CPT Codes'
];

const formatEligibleCptForCsv = (value) => {
  if (value == null || value === '') return 'N/A';
  if (Array.isArray(value)) return value.length ? value.join(', ') : 'N/A';
  return String(value);
};

const filterExportColumns = (headers, rows, selectedColumns) => {
  const selectedHeaderSet =
    Array.isArray(selectedColumns) && selectedColumns.length > 0
      ? new Set(selectedColumns)
      : null;
  if (!selectedHeaderSet) return { headers, rows };
  const keepIndices = [];
  headers.forEach((header, index) => {
    if (header === '#' || selectedHeaderSet.has(header)) {
      keepIndices.push(index);
    }
  });
  return {
    headers: keepIndices.map((index) => headers[index]),
    rows: rows.map((row) => keepIndices.map((index) => row[index]))
  };
};

const applyBillingFiltersToParams = (params, activeFilters) => {
  Object.keys(activeFilters || {}).forEach((key) => {
    if (key === 'selectData' || key === 'cptCode' || key === 'cptCodes') return;
    const value = activeFilters[key];
    if (value !== null && value !== undefined && String(value).trim() !== '') {
      params[key] = value;
    }
  });
  const selected = Array.isArray(activeFilters?.cptCodes)
    ? activeFilters.cptCodes.map(String).filter(Boolean)
    : String(activeFilters?.cptCode || '').trim()
      ? [String(activeFilters.cptCode).trim()]
      : [];
  if (!selected.length) return;
  // "All" means any listed CPT is applied — still send the codes so N/A (no CPT) patients are excluded.
  params.cptCode = selected.join(',');
};

const normalizeBillingStatus = (status) => {
  if (status == null || status === '') return '';
  const s = String(status);
  if (/^\d+$/.test(s)) {
    const map = { 2: 'Active', 3: 'Pending', 4: 'LOCK' };
    return map[s] || s;
  }
  const upper = s.toUpperCase();
  if (upper === 'LOCKED' || upper === 'L' || upper === 'LOST') return 'LOCK';
  return s;
};

const formatPersonName = (last, first) => {
  const name = `${last || ''}, ${first || ''}`.replace(/^,\s*|\s*,$/g, '').trim();
  return name || '';
};

const mapBillingPatientRow = (patient) => {
  const patientName = formatPersonName(patient.last_name, patient.first_name) || 'N/A';
  const dob = patient.date_of_birth || '';
  const rpmStartDate = patient.rpm_start_date || '';
  const rpmStartSortKey = normalizeDateOnly(rpmStartDate) || '';
  const uploadRaw =
    patient.upload_date_time ||
    patient.last_vital_upload_at ||
    patient.upload_date ||
    patient.last_upload_date ||
    patient.last_upload_at ||
    null;
  const uploadMs = uploadRaw ? new Date(uploadRaw).getTime() : 0;

  return {
    ...patient,
    status: normalizeBillingStatus(patient.status),
    patientName,
    dob,
    dobSort: dob ? new Date(dob).getTime() : 0,
    model: patient.model || 'N/A',
    serial: patient.serial || 'N/A',
    rpmStartDate,
    rpmStartDateSort: rpmStartSortKey,
    uploadDate: uploadRaw || '',
    uploadDateSort: Number.isNaN(uploadMs) ? 0 : uploadMs,
    measurementDays: Number(patient.measurement_days ?? 0),
    serviceTime: Number(patient.service_time ?? 0),
    eligibleCptCodes: patient.eligible_cpt_codes || 'N/A',
    clinicalMinutes: Number(patient.clinical_staff_minutes ?? 0),
    providerMinutes: Number(patient.provider_minutes ?? 0),
    isComplex: Boolean(patient.is_complex),
    billingPath: patient.billing_path || 'none',
    billingBlocked: Boolean(patient.billing_blocked),
    vitals: patient.vitals || '-',
    caregiverDisplay:
      patient.caregiver_name ||
      (Array.isArray(patient.caregiver_names) && patient.caregiver_names.length
        ? patient.caregiver_names.join(', ')
        : null) ||
      formatPersonName(patient.caregiver_last_name, patient.caregiver_first_name) ||
      'Caregiver N/A',
    providerDisplay: patient.provider_name || formatPersonName(patient.provider_last_name, patient.provider_first_name) || 'Provider N/A'
  };
};

const getBillingSortValue = (row, key) => {
  switch (key) {
    case 'status':
      return getStatusOrder(row.status);
    case 'patientName':
      return String(row.patientName || '').toLowerCase();
    case 'dob':
      return row.dobSort ?? 0;
    case 'rpmStartDate':
      return row.rpmStartDateSort ?? 0;
    case 'uploadDate':
      return row.uploadDateSort ?? 0;
    case 'measurementDays':
      return Number(row.measurementDays ?? 0);
    case 'clinicalMinutes':
      return Number(row.clinicalMinutes ?? 0);
    case 'serviceTime':
      return Number(row.serviceTime ?? 0);
    case 'providerMinutes':
      return Number(row.providerMinutes ?? 0);
    case 'caregiverDisplay':
      return String(row.caregiverDisplay || '').toLowerCase();
    case 'providerDisplay':
      return String(row.providerDisplay || '').toLowerCase();
    case 'eligibleCptCodes':
      return String(row.eligibleCptCodes || '').toLowerCase();
    case 'isComplex':
      return row.isComplex ? 1 : 0;
    case 'billingPath':
      return String(row.billingPath || '').toLowerCase();
    case 'model':
    case 'serial':
    case 'vitals':
      return String(row[key] ?? '').toLowerCase();
    default:
      return String(row[key] ?? '').toLowerCase();
  }
};

/** Sort by the active column first (asc/desc), then stable tiebreakers. */
const sortBillingPatients = (list, sortBy, sortOrder) => {
  const dir = sortOrder === 'desc' ? -1 : 1;
  return [...list].sort((a, b) => {
    const valueA = getBillingSortValue(a, sortBy);
    const valueB = getBillingSortValue(b, sortBy);

    if (typeof valueA === 'number' && typeof valueB === 'number') {
      if (valueA !== valueB) return dir * (valueA - valueB);
    } else {
      const cmp = String(valueA).localeCompare(String(valueB), undefined, {
        numeric: true,
        sensitivity: 'base'
      });
      if (cmp !== 0) return dir * cmp;
    }

    const statusA = getStatusOrder(a.status);
    const statusB = getStatusOrder(b.status);
    if (statusA !== statusB) return statusA - statusB;

    return String(a.patientName || '').localeCompare(String(b.patientName || ''), undefined, {
      sensitivity: 'base'
    });
  });
};

const formatBillingCycleLabel = (cycle) => {
  const range = `${formatDateOnlyUs(cycle.start) || cycle.start} ~ ${formatDateOnlyUs(cycle.end) || cycle.end}`;
  return cycle.cycleNumber ? `Cycle ${cycle.cycleNumber} · ${range}` : range;
};

const ManageBillingReport = () => {
  const { practiceId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const rawProg = searchParams.get('program')?.toLowerCase();
  const billingProgram = rawProg === 'pcm' ? 'pcm' : (rawProg === 'ccm' ? 'ccm' : 'rpm');
  const activeProgramTab = billingProgram === 'pcm' ? 'PCM' : (billingProgram === 'ccm' ? 'CCM' : 'RPM');
  const { updateBreadcrumbs, clearBreadcrumbs } = useBreadcrumb();

  useEffect(() => {
    return () => clearBreadcrumbs();
  }, [clearBreadcrumbs]);

  const includeOptionsNextFetchRef = useRef(true);
  const filtersRef = useRef(null);
  const appliedFiltersRef = useRef(null);
  const fetchGenerationRef = useRef(0);
  const skipNextEffectFetchRef = useRef(false);

  const [practice, setPractice] = useState(null);
  const [reportDates, setReportDates] = useState([]);
  const [tableLoading, setTableLoading] = useState(true);
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false);
  const [listReady, setListReady] = useState(false);
  const [patients, setPatients] = useState([]);
  const [pagination, setPagination] = useState({
    current_page: 1,
    per_page: 10,
    total: 0,
    total_pages: 1
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [entriesPerPage, setEntriesPerPage] = useState('10');
  const [showExportMenu, setShowExportMenu] = useState(false);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [showReportPreviewModal, setShowReportPreviewModal] = useState(false);
  const [reportPreviewData, setReportPreviewData] = useState(null);
  const [showPrintModal, setShowPrintModal] = useState(false);
  const [showLogSection, setShowLogSection] = useState(false);
  const logOverlayRef = useRef(null);
  const logContainerRef = useRef(null);
  useEventLogModalScrollLock(showLogSection, logOverlayRef, logContainerRef);
  const [showGenerateBilling, setShowGenerateBilling] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [sortBy, setSortBy] = useState('patientName');
  const [sortOrder, setSortOrder] = useState('asc');

  const rpmStartInputRef = useRef(null);
  const rpmEndInputRef = useRef(null);
  const cptDropdownRef = useRef(null);
  const [isCptDropdownOpen, setIsCptDropdownOpen] = useState(false);
  const [serviceDateMode, setServiceDateMode] = useState('');

  const handlePatientRowClick = useCallback((patient) => {
    const patientId = resolvePatientRecordId(patient);
    const practiceIdForRoute = resolvePatientPracticeId(patient, practiceId);
    const path = buildPatientDetailsPath(practiceIdForRoute, patientId);
    if (!path) {
      toast.error('Unable to open patient details.');
      return;
    }
    navigate(path);
  }, [navigate, practiceId]);

  const openDatePicker = (ref) => {
    ref?.current?.setOpen?.(true);
  };

  // Filter states
  const [filters, setFilters] = useState({
    cptCodes: [],
    model: '',
    status: '',
    patientName: '',
    serial: '',
    insurance: '',
    serviceTime: '',
    measurementDays: '',
    caregiver: '',
    provider: '',
    serviceDateMode: '',
    rpmStartDate: '',
    rpmEndDate: ''
  });

  // Dropdown options (these would come from API in real implementation)
  const [dropdownOptions, setDropdownOptions] = useState({
    cptCodes: [],
    models: [],
    statuses: ['Active', 'Pending', 'LOCK'],
    patients: [],
    measurementDays: ['0', '1-10', '11-20', '21-30', '31+'],
    caregivers: [],
    providers: []
  });

  const buildDefaultFilters = () => ({
    cptCodes: [],
    model: '',
    status: '',
    patientName: '',
    serial: '',
    insurance: '',
    serviceTime: '',
    measurementDays: '',
    caregiver: '',
    provider: '',
    serviceDateMode: '',
    // Service date range stays blank until the user selects it
    rpmStartDate: '',
    rpmEndDate: ''
  });

  const applyPracticeContext = useCallback((practiceData, { resetFilters = false } = {}) => {
    if (!practiceData) return;
    setPractice(practiceData);
    updateBreadcrumbs([
      { label: 'Billing', path: '/billing' },
      { label: practiceData.practice_name || 'Billing Report', path: null, active: true }
    ]);
    if (resetFilters) {
      setFilters(buildDefaultFilters());
      setServiceDateMode('');
    }
    writeBillingPracticeCache(practiceData);
  }, [updateBreadcrumbs]);

  const applyDropdownOptions = (options, program = billingProgram) => {
    if (!options) return;
    const cptCodes = options.cptCodes?.length
      ? options.cptCodes
      : getBillingCptCodesForProgram(program);
    setDropdownOptions({
      cptCodes,
      models: options.models || [],
      statuses: ['Active', 'Pending', 'LOCK'],
      patients: options.patients || [],
      measurementDays: ['0', '1-10', '11-20', '21-30', '31+'],
      caregivers: options.caregivers || [],
      providers: options.providers || []
    });
  };

  filtersRef.current = filters;

  useEffect(() => {
    if (!practiceId) return;

    const prefetched =
      (location.state?.practice && String(location.state.practice.id) === String(practiceId)
        ? location.state.practice
        : null) ||
      readBillingPracticeCache(practiceId);

    if (prefetched) {
      applyPracticeContext(prefetched, { resetFilters: true });
    } else {
      updateBreadcrumbs([
        { label: 'Billing', path: '/billing' },
        { label: 'Billing Report', path: null, active: true }
      ]);
    }

    includeOptionsNextFetchRef.current = true;
    setCurrentPage(1);
    setSearchTerm('');
    setDebouncedSearch('');
    setHasLoadedOnce(false);
    setReportDates([]);
    setListReady(true);
  }, [practiceId, location.state, applyPracticeContext, updateBreadcrumbs]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm);
      setCurrentPage(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  useEffect(() => {
    includeOptionsNextFetchRef.current = true;
    setDropdownOptions((prev) => ({
      ...prev,
      cptCodes: getBillingCptCodesForProgram(billingProgram)
    }));
    setFilters((prev) => ({
      ...prev,
      cptCodes: [],
      ...(billingProgram === 'ccm' || billingProgram === 'pcm'
        ? { model: '', serial: '', measurementDays: '' }
        : {})
    }));
    setIsCptDropdownOpen(false);
  }, [billingProgram]);

  useEffect(() => {
    if (!practiceId || !listReady) return;
    if (skipNextEffectFetchRef.current) {
      skipNextEffectFetchRef.current = false;
      return;
    }
    fetchBillingData();
  }, [practiceId, listReady, currentPage, entriesPerPage, debouncedSearch, billingProgram, sortBy, sortOrder]);

  // Close export menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showExportMenu && !event.target.closest('.export-dropdown')) {
        setShowExportMenu(false);
      }
    };

    if (showExportMenu) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [showExportMenu]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (cptDropdownRef.current && !cptDropdownRef.current.contains(event.target)) {
        setIsCptDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const dedupePatientsById = (rows) => {
    if (!Array.isArray(rows)) return [];
    const seen = new Set();
    return rows.filter((row) => {
      const key = row?.id;
      if (key == null) return true;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  };

  const fetchBillingData = async (options = {}) => {
    if (!practiceId) return;

    const fetchId = ++fetchGenerationRef.current;
    const activeFilters = options.filtersOverride || filtersRef.current || filters;
    const page = options.page ?? currentPage;
    const limit =
      options.limit ??
      (entriesPerPage === 'all' ? 1000 : Number.parseInt(entriesPerPage, 10) || 10);
    const includeOptions =
      options.includeOptions ?? includeOptionsNextFetchRef.current;
    const searchValue =
      options.search !== undefined ? options.search : debouncedSearch;

    setTableLoading(true);
    try {
      const params = { page, limit, program: billingProgram, sortBy, sortOrder };

      if (includeOptions) {
        params.includeOptions = '1';
      }

      if (String(searchValue || '').trim()) {
        params.search = String(searchValue).trim();
      }

      applyBillingFiltersToParams(params, activeFilters);
      const effectiveServiceDateMode = activeFilters.serviceDateMode || serviceDateMode || '';
      if (effectiveServiceDateMode) {
        params.serviceDateMode = effectiveServiceDateMode;
      }

      const response = await billingService.getBillingReports(practiceId, params);
      if (fetchId !== fetchGenerationRef.current) return;

      const rows = response?.data?.patients ?? response?.patients;
      if (response?.success) {
        appliedFiltersRef.current = { ...activeFilters };
        if (response.data?.practice) {
          applyPracticeContext(response.data.practice);
        }
        if (Array.isArray(response.data?.reportDates)) {
          setReportDates(response.data.reportDates);
        }
        if (response.data?.options) {
          applyDropdownOptions(response.data.options, billingProgram);
          includeOptionsNextFetchRef.current = false;
        }
        // Always replace (never append) so reset/refetch cannot stack clones
        setPatients(dedupePatientsById(rows));
        if (response?.data?.pagination) {
          setPagination(response.data.pagination);
        }
      } else {
        toast.error(response?.message || 'Failed to load billing data');
      }
    } catch (error) {
      if (fetchId !== fetchGenerationRef.current) return;
      console.error('Error fetching billing data:', error);
      toast.error('Failed to load billing data');
    } finally {
      if (fetchId === fetchGenerationRef.current) {
        setTableLoading(false);
        setHasLoadedOnce(true);
      }
    }
  };

  const fetchAllBillingPatients = async (filtersOverride = null, { ignoreSearch = false } = {}) => {
    if (!practiceId) return [];

    const activeFilters =
      filtersOverride || appliedFiltersRef.current || filtersRef.current || filters;
  const params = { exportAll: '1', program: billingProgram, sortBy, sortOrder };

    if (!ignoreSearch && debouncedSearch.trim()) {
      params.search = debouncedSearch.trim();
    }

    applyBillingFiltersToParams(params, activeFilters);
    const effectiveServiceDateMode = activeFilters.serviceDateMode || serviceDateMode || '';
    if (effectiveServiceDateMode) {
      params.serviceDateMode = effectiveServiceDateMode;
    }

    const response = await billingService.getBillingReports(practiceId, params);
    const rows = response?.data?.patients ?? response?.patients;
    return Array.isArray(rows) ? rows.map(mapBillingPatientRow) : [];
  };

  const handleFilterChange = (field, value) => {
    setFilters((prev) => ({
      ...prev,
      [field]: value
    }));
  };

  const handleServiceStartDateChange = (value) => {
    const today = getTodayYmd();
    const start = value && value > today ? today : (value || '');
    let nextEnd = filters.rpmEndDate;
    if (nextEnd && start && nextEnd < start) {
      nextEnd = '';
    }
    const nextFilters = {
      ...filters,
      serviceDateMode: 'custom',
      rpmStartDate: start,
      rpmEndDate: nextEnd
    };
    filtersRef.current = nextFilters;
    setFilters(nextFilters);
    if (start && nextEnd) {
      setPatients([]);
      appliedFiltersRef.current = nextFilters;
      if (currentPage !== 1) {
        skipNextEffectFetchRef.current = true;
      }
      setCurrentPage(1);
      fetchBillingData({ page: 1, filtersOverride: nextFilters, includeOptions: false });
    }
  };

  const handleServiceEndDateChange = (value) => {
    const today = getTodayYmd();
    let end = value || '';
    if (end && end > today) end = today;
    if (filters.rpmStartDate && end && end < filters.rpmStartDate) {
      return;
    }
    const nextFilters = {
      ...filters,
      serviceDateMode: 'custom',
      rpmEndDate: end
    };
    filtersRef.current = nextFilters;
    setFilters(nextFilters);
    if (nextFilters.rpmStartDate && end) {
      setPatients([]);
      appliedFiltersRef.current = nextFilters;
      if (currentPage !== 1) {
        skipNextEffectFetchRef.current = true;
      }
      setCurrentPage(1);
      fetchBillingData({ page: 1, filtersOverride: nextFilters, includeOptions: false });
    }
  };

  const billingCycles = useMemo(() => {
    const isNotRpm = billingProgram === 'ccm' || billingProgram === 'pcm';
    const originStart = isNotRpm
      ? (practice?.billing_from_date || practice?.rpm_start_date)
      : (practice?.rpm_start_date || practice?.billing_from_date);
    const currentStart = practice?.billing_from_date || practice?.rpm_start_date;
    const currentEnd = practice?.billing_to_date || practice?.rpm_end_date;
    const periodDays = Number.parseInt(String(practice?.rpm_validity_days), 10) || DEFAULT_BILLING_PERIOD_DAYS;
    const untilYmd = getTodayYmd();
    return buildBillingCycleList({
      originStart,
      periodDays,
      untilYmd,
      savedCycles: reportDates,
      currentStart,
      currentEnd
    });
  }, [billingProgram, practice, reportDates]);

  const selectedBillingCycleKey = useMemo(
    () => findBillingCycleKey(billingCycles, filters.rpmStartDate, filters.rpmEndDate),
    [billingCycles, filters.rpmStartDate, filters.rpmEndDate]
  );

  const handleBillingCycleSelect = (cycleKey) => {
    if (!cycleKey) return;
    const cycle = billingCycles.find((item) => item.key === cycleKey);
    if (!cycle) return;
    const nextFilters = {
      ...filters,
      serviceDateMode: 'cycle',
      rpmStartDate: cycle.start,
      rpmEndDate: cycle.end
    };
    // Clear stale patient rows so previous cycle data doesn't remain visible
    setPatients([]);
    filtersRef.current = nextFilters;
    appliedFiltersRef.current = nextFilters;
    setFilters(nextFilters);
    if (currentPage !== 1) {
      skipNextEffectFetchRef.current = true;
    }
    setCurrentPage(1);
    fetchBillingData({ page: 1, filtersOverride: nextFilters, includeOptions: false });
  };

  const handleServiceDateModeChange = (mode) => {
    setServiceDateMode(mode);
    const hadDates = Boolean(filters.rpmStartDate || filters.rpmEndDate);
    const nextFilters = {
      ...filters,
      serviceDateMode: mode,
      rpmStartDate: '',
      rpmEndDate: ''
    };
    filtersRef.current = nextFilters;
    setFilters(nextFilters);
    if (hadDates) {
      setPatients([]);
      appliedFiltersRef.current = nextFilters;
      if (currentPage !== 1) {
        skipNextEffectFetchRef.current = true;
      }
      setCurrentPage(1);
      fetchBillingData({ page: 1, filtersOverride: nextFilters, includeOptions: false });
    }
  };

  const toggleCptCode = (code) => {
    setFilters((prev) => {
      const current = Array.isArray(prev.cptCodes) ? prev.cptCodes : [];
      const exists = current.includes(code);
      return {
        ...prev,
        cptCodes: exists ? current.filter((value) => value !== code) : [...current, code]
      };
    });
  };

  const toggleCptAll = () => {
    const allCodes = dropdownOptions.cptCodes || [];
    setFilters((prev) => {
      const current = Array.isArray(prev.cptCodes) ? prev.cptCodes : [];
      const allSelected = allCodes.length > 0 && allCodes.every((code) => current.includes(code));
      return {
        ...prev,
        cptCodes: allSelected ? [] : [...allCodes]
      };
    });
  };

  const handleQuery = () => {
    const today = getTodayYmd();
    if (filters.rpmStartDate && filters.rpmStartDate > today) {
      toast.warning('Start date cannot be in the future');
      return;
    }
    if (filters.rpmEndDate && filters.rpmEndDate > today) {
      toast.warning('End date cannot be in the future');
      return;
    }
    if (filters.rpmStartDate && filters.rpmEndDate && filters.rpmEndDate < filters.rpmStartDate) {
      toast.warning('End date cannot be before the start date');
      return;
    }
    const queryFilters = {
      ...filters,
      serviceDateMode: serviceDateMode || filters.serviceDateMode || ''
    };
    filtersRef.current = queryFilters;
    appliedFiltersRef.current = queryFilters;
    setFilters(queryFilters);
    // Avoid double-fetch when setCurrentPage(1) also triggers the list effect
    if (currentPage !== 1) {
      skipNextEffectFetchRef.current = true;
    }
    setCurrentPage(1);
    fetchBillingData({ page: 1, filtersOverride: queryFilters, includeOptions: false });
  };

  const handleReset = async () => {
    const defaultFilters = buildDefaultFilters();
    // Clear local grid state before reload so reset is idempotent (no stacked rows)
    setPatients([]);
    appliedFiltersRef.current = { ...defaultFilters };
    filtersRef.current = defaultFilters;
    setFilters(defaultFilters);
    setServiceDateMode('');
    setSearchTerm('');
    const willTriggerEffectFetch = currentPage !== 1 || debouncedSearch !== '';
    if (willTriggerEffectFetch) {
      skipNextEffectFetchRef.current = true;
    }
    setDebouncedSearch('');
    setCurrentPage(1);
    setPagination((prev) => ({
      ...prev,
      current_page: 1,
      total: 0,
      total_pages: 1
    }));
    await fetchBillingData({
      page: 1,
      filtersOverride: defaultFilters,
      search: '',
      includeOptions: false
    });
  };

  const handlePrint = () => {
    if (!patients.length && Number(pagination?.total || 0) === 0) {
      toast.warning('No data to print');
      return;
    }
    setShowPrintModal(true);
  };

  const handlePerformPrint = async (selectedColumns) => {
    setShowPrintModal(false);
    setTableLoading(true);
    try {
      const allFilteredPatients = await fetchAllBillingPatients();

      if (allFilteredPatients.length === 0) {
        toast.warning('No data to print');
        return;
      }

      const printWindow = window.open('', '_blank');
      if (!printWindow) {
        toast.error('Please allow popups to print');
        return;
      }

      const isPcm = billingProgram === 'pcm';
      const isCcm = billingProgram === 'ccm';

      let headers;
      let rows;
      if (isPcm) {
        headers = ['#', ...PCM_EMAIL_COLUMNS];
        rows = allFilteredPatients.map((patient, idx) => [
          idx + 1,
          patient.status || 'N/A',
          patient.patientName || 'N/A',
          patient.dob ? formatDate(patient.dob) : 'N/A',
          patient.clinicalMinutes ?? 0,
          patient.providerMinutes ?? 0,
          patient.serviceTime ?? 0,
          patient.caregiverDisplay || 'N/A',
          patient.providerDisplay || 'N/A',
          formatEligibleCptForCsv(patient.eligibleCptCodes)
        ]);
      } else if (isCcm) {
        headers = ['#', ...CCM_EMAIL_COLUMNS];
        rows = allFilteredPatients.map((patient, idx) => [
          idx + 1,
          patient.status || 'N/A',
          patient.patientName || 'N/A',
          patient.dob ? formatDate(patient.dob) : 'N/A',
          patient.clinicalMinutes ?? 0,
          patient.providerMinutes ?? 0,
          patient.isComplex ? 'Complex' : 'Non-Complex',
          patient.billingPath || '—',
          patient.caregiverDisplay || 'N/A',
          patient.providerDisplay || 'N/A',
          formatEligibleCptForCsv(patient.eligibleCptCodes)
        ]);
      } else {
        headers = ['#', ...RPM_EMAIL_COLUMNS];
        rows = allFilteredPatients.map((patient, idx) => [
          idx + 1,
          patient.status || 'N/A',
          patient.vitals || 'N/A',
          patient.patientName || 'N/A',
          patient.dob ? formatDate(patient.dob) : 'N/A',
          patient.model || 'N/A',
          patient.serial || 'N/A',
          patient.rpmStartDate ? formatDate(patient.rpmStartDate) : 'N/A',
          patient.uploadDate ? formatDateTime(patient.uploadDate) : '',
          patient.measurementDays ?? 0,
          patient.serviceTime ?? 0,
          patient.caregiverDisplay || 'N/A',
          patient.providerDisplay || 'N/A',
          formatEligibleCptForCsv(patient.eligibleCptCodes)
        ]);
      }

      ({ headers, rows } = filterExportColumns(headers, rows, selectedColumns));
      const nameIdx = headers.indexOf('Patient Name');

      const printContent = `
        <!DOCTYPE html>
        <html>
          <head>
            <title>${activeProgramTab} Billing Report - ${practice?.practice_name || 'Report'}</title>
            <style>
              @media print {
                @page {
                  margin: 0.5in;
                  size: letter landscape;
                }
                body {
                  font-family: Arial, sans-serif;
                  font-size: 10px;
                  margin: 0;
                  padding: 0;
                }
                .print-header {
                  text-align: center;
                  margin-bottom: 20px;
                  border-bottom: 2px solid #000;
                  padding-bottom: 10px;
                }
                .print-header h2 {
                  margin: 0 0 5px 0;
                  font-size: 18px;
                }
                .print-header .billing-period {
                  font-size: 12px;
                  color: #666;
                }
                .print-date {
                  text-align: right;
                  font-size: 10px;
                  margin-bottom: 10px;
                }
                table {
                  width: 100%;
                  border-collapse: collapse;
                  font-size: 9px;
                }
                th, td {
                  border: 1px solid #000;
                  padding: 4px;
                  text-align: left;
                }
                th {
                  background-color: #f0f0f0;
                  font-weight: bold;
                }
                tr:nth-child(even) {
                  background-color: #f9f9f9;
                }
                .no-print {
                  display: none;
                }
              }
            </style>
          </head>
          <body>
            <div class="print-header">
              <h2>${practice?.practice_name || 'Billing Report'} - ${activeProgramTab}</h2>
              <div class="billing-period">Billing Period: ${getBillingPeriod()}</div>
            </div>
            <div class="print-date">Generated: ${new Date().toLocaleString()}</div>
            <table>
              <thead>
                <tr>
                  ${headers.map((h) => `<th>${h}</th>`).join('')}
                </tr>
              </thead>
              <tbody>
                ${rows.map((row) => `
                  <tr>
                    ${row.map((cell, cIdx) => cIdx === nameIdx ? `<td><strong>${cell}</strong></td>` : `<td>${cell}</td>`).join('')}
                  </tr>
                `).join('')}
              </tbody>
            </table>
            <div style="margin-top: 20px; font-size: 10px; text-align: center;">
              Total Records: ${allFilteredPatients.length}
            </div>
          </body>
        </html>
      `;

      printWindow.document.write(printContent);
      printWindow.document.close();
      printWindow.focus();

      setTimeout(() => {
        printWindow.print();
        printWindow.close();
      }, 250);
    } catch (error) {
      console.error('Error printing billing report:', error);
      toast.error('Failed to print billing report');
    } finally {
      setTableLoading(false);
    }
  };

  const handleExport = async (format = 'csv') => {
    setTableLoading(true);
    try {
      const allFilteredPatients = await fetchAllBillingPatients();

      if (allFilteredPatients.length === 0) {
        toast.warning('No data to export');
        return;
      }

      const isPcm = billingProgram === 'pcm';
      const isCcm = billingProgram === 'ccm';

      let headers;
      let rows;
      if (isPcm) {
        headers = ['#', 'Status', 'Patient Name', 'DOB', 'Clinical Min', 'Provider Min', 'Service Time', 'Caregiver', 'Provider', 'Eligible CPT Codes'];
        rows = allFilteredPatients.map((patient, idx) => [
          idx + 1,
          patient.status || 'N/A',
          patient.patientName || 'N/A',
          patient.dob ? formatDate(patient.dob) : 'N/A',
          patient.clinicalMinutes ?? 0,
          patient.providerMinutes ?? 0,
          patient.serviceTime ?? 0,
          patient.caregiverDisplay || 'N/A',
          patient.providerDisplay || 'N/A',
          patient.eligibleCptCodes || 'N/A'
        ]);
      } else if (isCcm) {
        headers = ['#', 'Status', 'Patient Name', 'DOB', 'Clinical Min', 'Provider Min', 'Complexity', 'Billing Path', 'Caregiver', 'Provider', 'Eligible CPT Codes'];
        rows = allFilteredPatients.map((patient, idx) => [
          idx + 1,
          patient.status || 'N/A',
          patient.patientName || 'N/A',
          patient.dob ? formatDate(patient.dob) : 'N/A',
          patient.clinicalMinutes ?? 0,
          patient.providerMinutes ?? 0,
          patient.isComplex ? 'Complex' : 'Non-Complex',
          patient.billingPath || '—',
          patient.caregiverDisplay || 'N/A',
          patient.providerDisplay || 'N/A',
          patient.eligibleCptCodes || 'N/A'
        ]);
      } else {
        headers = ['#', 'Status', 'Vitals', 'Patient Name', 'DOB', 'Modal #', 'Serial #', 'RPM Start Date', 'Upload Date/Time', 'Measurement Days', 'Service Time', 'Caregiver', 'Provider', 'Eligible Cpt Codes'];
        rows = allFilteredPatients.map((patient, idx) => [
          idx + 1,
          patient.status || 'N/A',
          patient.vitals || 'N/A',
          patient.patientName || 'N/A',
          patient.dob ? formatDate(patient.dob) : 'N/A',
          patient.model || 'N/A',
          patient.serial || 'N/A',
          patient.rpmStartDate ? formatDate(patient.rpmStartDate) : 'N/A',
          patient.uploadDate ? formatDateTime(patient.uploadDate) : 'N/A',
          patient.measurementDays ?? 0,
          patient.serviceTime ?? 0,
          patient.caregiverDisplay || 'N/A',
          patient.providerDisplay || 'N/A',
          patient.eligibleCptCodes || 'N/A'
        ]);
      }

      if (format === 'csv') {
        const csvContent = [
          headers.join(','),
          ...rows.map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(','))
        ].join('\n');

        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        const url = URL.createObjectURL(blob);
        link.setAttribute('href', url);
        link.setAttribute('download', `billing-report-${practice?.practice_name || 'report'}-${billingProgram}-${new Date().toISOString().split('T')[0]}.csv`);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        billingService.logBillingExport(practiceId, { format: 'csv', source: 'Billing Report' });
        toast.success('Report exported to CSV');
      } else if (format === 'pdf') {
        try {
          exportColoredTablePdf({
            filename: `billing-report-${practice?.practice_name || 'report'}-${billingProgram}-${new Date().toISOString().split('T')[0]}.pdf`,
            title: `${practice?.practice_name || 'Billing Report'} (${activeProgramTab})`,
            metaLines: [
              `Billing Period: ${getBillingPeriod()}`,
              `Generated: ${new Date().toLocaleString()}`,
              `Total Records: ${allFilteredPatients.length}`
            ],
            headers,
            rows: rows.map((row) => row.map((cell) => String(cell ?? ''))),
            headerBg: '#0077b6',
            fontSize: 7,
            marginX: 22,
            columnStyles: {
              0: { cellWidth: 22, halign: 'center' },
              1: { cellWidth: 42 },
              3: { cellWidth: 54 }
            }
          });
          billingService.logBillingExport(practiceId, { format: 'pdf', source: 'Billing Report' });
          toast.success('Report exported to PDF');
        } catch (error) {
          console.error('Error exporting PDF:', error);
          toast.error('Failed to export PDF');
        }
      }
    } catch (error) {
      console.error('Error exporting billing report:', error);
      toast.error('Failed to export billing report');
    } finally {
      setTableLoading(false);
    }
  };

  const formatDate = (dateString) => formatDateOnlyUs(dateString) || 'N/A';

  const formatDateTime = (dateString) => {
    if (!dateString) return '';
    const raw = String(dateString).trim();
    const match = raw.match(/^(\d{4})-(\d{2})-(\d{2})(?:[T\s](\d{2}):(\d{2})(?::(\d{2}))?)?/);
    if (!match) {
      const date = new Date(dateString);
      if (Number.isNaN(date.getTime())) return '';
      return date.toLocaleString('en-US', {
        month: '2-digit',
        day: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    }

    const [, yyyy, mm, dd, hhStr, minStr] = match;
    if (hhStr !== undefined && minStr !== undefined) {
      let hour = Number.parseInt(hhStr, 10);
      const ampm = hour >= 12 ? 'PM' : 'AM';
      hour = hour % 12 || 12;
      const formattedHour = String(hour).padStart(2, '0');
      return `${mm}/${dd}/${yyyy}, ${formattedHour}:${minStr} ${ampm}`;
    }

    return `${mm}/${dd}/${yyyy}`;
  };

  const getBillingPeriodBounds = () => {
    if (filters?.rpmStartDate && filters?.rpmEndDate) {
      return { start: filters.rpmStartDate, end: filters.rpmEndDate };
    }
    const active = appliedFiltersRef.current || filtersRef.current || filters;
    if (active?.rpmStartDate && active?.rpmEndDate) {
      return { start: active.rpmStartDate, end: active.rpmEndDate };
    }
    const period = getPracticeBillingPeriod(practice);
    return { start: period.start || '', end: period.end || '' };
  };

  const getBillingPeriod = () => {
    const { start, end } = getBillingPeriodBounds();
    if (start && end) {
      return `${formatDate(start)} ~ ${formatDate(end)}`;
    }
    return 'N/A';
  };

  const handleEmail = () => {
    if (!practiceId) {
      toast.warning('Practice is required to email the report');
      return;
    }
    setShowEmailModal(true);
  };

  const getBillingReportTableData = (patients, selectedColumns) => {
    const patientList = Array.isArray(patients) ? patients : [];
    const isPcm = billingProgram === 'pcm';
    const isCcm = billingProgram === 'ccm';
    let headers;
    let rows;
    if (isPcm) {
      headers = ['#', ...PCM_EMAIL_COLUMNS];
      rows = patientList.map((patient, idx) => [
        idx + 1,
        patient.status || 'N/A',
        patient.patientName || 'N/A',
        patient.dob ? formatDate(patient.dob) : 'N/A',
        patient.clinicalMinutes ?? 0,
        patient.providerMinutes ?? 0,
        patient.serviceTime ?? 0,
        patient.caregiverDisplay || 'N/A',
        patient.providerDisplay || 'N/A',
        formatEligibleCptForCsv(patient.eligibleCptCodes)
      ]);
    } else if (isCcm) {
      headers = ['#', ...CCM_EMAIL_COLUMNS];
      rows = patientList.map((patient, idx) => [
        idx + 1,
        patient.status || 'N/A',
        patient.patientName || 'N/A',
        patient.dob ? formatDate(patient.dob) : 'N/A',
        patient.clinicalMinutes ?? 0,
        patient.providerMinutes ?? 0,
        patient.isComplex ? 'Complex' : 'Non-Complex',
        patient.billingPath || '—',
        patient.caregiverDisplay || 'N/A',
        patient.providerDisplay || 'N/A',
        formatEligibleCptForCsv(patient.eligibleCptCodes)
      ]);
    } else {
      headers = ['#', ...RPM_EMAIL_COLUMNS];
      rows = patientList.map((patient, idx) => [
        idx + 1,
        patient.status || 'N/A',
        patient.vitals || 'N/A',
        patient.patientName || 'N/A',
        patient.dob ? formatDate(patient.dob) : 'N/A',
        patient.model || 'N/A',
        patient.serial || 'N/A',
        patient.rpmStartDate ? formatDate(patient.rpmStartDate) : 'N/A',
        patient.uploadDate ? formatDateTime(patient.uploadDate) : 'N/A',
        patient.measurementDays ?? 0,
        patient.serviceTime ?? 0,
        patient.caregiverDisplay || 'N/A',
        patient.providerDisplay || 'N/A',
        formatEligibleCptForCsv(patient.eligibleCptCodes)
      ]);
    }
    return filterExportColumns(headers, rows, selectedColumns);
  };

  const handlePreviewEmailReport = async (selectedColumns) => {
    let patients = [];
    try {
      patients = await fetchAllBillingPatients();
    } catch (err) {
      console.error('Error fetching patients for preview:', err);
      patients = filteredPatients || [];
    }
    if (!patients || patients.length === 0) {
      toast.info('No patients available to preview in report');
      return;
    }
    const cols = Array.isArray(selectedColumns) && selectedColumns.length > 0
      ? selectedColumns
      : availableEmailColumns;
    const { headers, rows } = getBillingReportTableData(patients, cols);
    setReportPreviewData({
      headers,
      rows,
      count: patients.length,
      practiceName: practice?.practice_name || 'Practice',
      program: activeProgramTab,
      billingPeriod: getBillingPeriod(),
    });
    setShowReportPreviewModal(true);
  };

  const handleDownloadPreviewCsv = () => {
    if (!reportPreviewData) return;
    const { headers, rows } = reportPreviewData;
    const csvContent = [
      headers.join(','),
      ...rows.map((row) => row.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(','))
    ].join('\n');
    const safeName = String(practice?.practice_name || 'report')
      .replace(/[^\w-]+/g, '_')
      .replace(/^_+|_+$/g, '') || 'report';
    const filename = `billing-report-${safeName}-${activeProgramTab.toLowerCase()}-${new Date().toISOString().split('T')[0]}.csv`;
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const buildBillingCsvAttachment = async (selectedColumns) => {
    const allFilteredPatients = await fetchAllBillingPatients();
    const { headers, rows } = getBillingReportTableData(allFilteredPatients, selectedColumns);
    const csvContent = [
      headers.join(','),
      ...rows.map((row) => row.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(','))
    ].join('\n');
    const safeName = String(practice?.practice_name || 'report')
      .replace(/[^\w-]+/g, '_')
      .replace(/^_+|_+$/g, '') || 'report';
    const filename = `billing-report-${safeName}-${activeProgramTab.toLowerCase()}-${new Date().toISOString().split('T')[0]}.csv`;
    const file = new File([csvContent], filename, { type: 'text/csv;charset=utf-8;' });
    return { file, count: allFilteredPatients.length };
  };

  const handleSendEmail = async (emailData) => {
    try {
      const { file, count } = await buildBillingCsvAttachment(emailData.selectedColumns);
      const userAttachments = Array.isArray(emailData.attachments) ? emailData.attachments : [];
      const attachments = [file, ...userAttachments];
      const contentWithSummary = [
        String(emailData.content || '').trim(),
        '',
        `Attached CSV includes ${count} patient row(s) for ${practice?.practice_name || 'this practice'}.`,
        `Program: ${activeProgramTab}`,
        `Billing Period: ${getBillingPeriod()}`
      ]
        .filter(Boolean)
        .join('\n');

      const res = await sendReportEmail(
        { ...emailData, content: contentWithSummary, attachments },
        { event_id: 12 }
      );
      if (!res.success) {
        throw new Error(res.error || res.message || 'Failed to send email');
      }
      toast.success('Billing report email queued successfully');
      setShowEmailModal(false);
    } catch (err) {
      console.error('Error sending billing report email:', err);
      toast.error(err?.message || 'Failed to send billing report email');
    }
  };

  const defaultEmailSubject = useMemo(() => {
    const practiceName = practice?.practice_name || 'Practice';
    return `${activeProgramTab} Billing Report - ${practiceName} (${getBillingPeriod()})`;
  }, [practice?.practice_name, activeProgramTab, filters.rpmStartDate, filters.rpmEndDate, practice]);

  const defaultEmailContent = useMemo(() => {
    const practiceName = practice?.practice_name || 'Practice';
    const total = Number(pagination?.total || patients.length || 0);
    return [
      `Please find the ${activeProgramTab} billing report for ${practiceName}.`,
      '',
      `Billing Period: ${getBillingPeriod()}`,
      `Total Patients (current filters): ${total}`,
      '',
      'The full patient list is attached as a CSV file.'
    ].join('\n');
  }, [practice?.practice_name, activeProgramTab, pagination?.total, patients.length, filters.rpmStartDate, filters.rpmEndDate, practice]);

  const handleSort = (key) => {
    if (!key) return;
    if (sortBy === key) {
      setSortOrder((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortBy(key);
      setSortOrder('asc');
    }
    if (currentPage !== 1) {
      setCurrentPage(1);
    }
  };

  const mappedPatients = useMemo(
    () => (Array.isArray(patients) ? patients.map(mapBillingPatientRow) : []),
    [patients]
  );

  const filteredPatients = useMemo(
    () => sortBillingPatients(mappedPatients, sortBy, sortOrder),
    [mappedPatients, sortBy, sortOrder]
  );

  const pageSize = useMemo(() => {
    if (entriesPerPage === 'all') return Math.max(pagination.per_page || mappedPatients.length, 1);
    const n = Number.parseInt(entriesPerPage, 10);
    return Number.isFinite(n) && n > 0 ? n : 10;
  }, [entriesPerPage, pagination.per_page, mappedPatients.length]);

  const totalPages = Math.max(1, pagination.total_pages || 1);

  const handlePageChange = (page) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages]);

  const rpmBillingTableColumns = useMemo(() => [
    {
      key: 'rowNo',
      title: '#',
      className: 'reusable-table-centered-col',
      render: (_row, rowIndex) => (currentPage - 1) * pageSize + rowIndex + 1
    },
    {
      key: 'status',
      title: 'Status',
      sortable: true,
      className: 'reusable-table-centered-col',
      render: (row) => <StatusBadge status={row.status || 'N/A'} />
    },
    { key: 'vitals', title: 'Vitals', sortable: true, render: (row) => row.vitals || '-' },
    {
      key: 'patientName',
      title: 'Patient Name',
      sortable: true,
      render: (row) => (
        <a
          href="#"
          className="patient-management-name-link"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            handlePatientRowClick(row);
          }}
        >
          <strong>{row.patientName}</strong>
        </a>
      )
    },
    {
      key: 'dob',
      title: 'DOB',
      sortable: true,
      render: (row) => (row.dob ? formatDate(row.dob) : 'N/A')
    },
    { key: 'model', title: 'Modal #', sortable: true, render: (row) => row.model || 'N/A' },
    { key: 'serial', title: 'Serial #', sortable: true, render: (row) => row.serial || 'N/A' },
    {
      key: 'rpmStartDate',
      title: 'RPM Start Date',
      sortable: true,
      render: (row) => (row.rpmStartDate ? formatDate(row.rpmStartDate) : 'N/A')
    },
    {
      key: 'uploadDate',
      title: 'Upload Date/Time',
      sortable: true,
      render: (row) => (row.uploadDate ? formatDateTime(row.uploadDate) : '')
    },
    {
      key: 'measurementDays',
      title: 'Measurement Days',
      sortable: true,
      className: 'reusable-table-centered-col',
      render: (row) => String(row.measurementDays ?? 0)
    },
    {
      key: 'serviceTime',
      title: 'Service Time',
      sortable: true,
      className: 'reusable-table-centered-col',
      render: (row) => String(row.serviceTime ?? 0)
    },
    {
      key: 'caregiverDisplay',
      title: 'Caregiver',
      sortable: true,
      render: (row) => row.caregiverDisplay || 'Caregiver N/A'
    },
    {
      key: 'providerDisplay',
      title: 'Provider',
      sortable: true,
      render: (row) => row.providerDisplay || 'Provider N/A'
    },
    {
      key: 'eligibleCptCodes',
      title: 'Eligible Cpt Codes',
      sortable: true,
      render: (row) => row.eligibleCptCodes || 'N/A'
    }
  ], [currentPage, pageSize, handlePatientRowClick]);

  const ccmBillingTableColumns = useMemo(() => [
    {
      key: 'rowNo',
      title: '#',
      className: 'reusable-table-centered-col',
      render: (_row, rowIndex) => (currentPage - 1) * pageSize + rowIndex + 1
    },
    {
      key: 'status',
      title: 'Status',
      sortable: true,
      className: 'reusable-table-centered-col',
      render: (row) => <StatusBadge status={row.status || 'N/A'} />
    },
    {
      key: 'patientName',
      title: 'Patient Name',
      sortable: true,
      render: (row) => (
        <a
          href="#"
          className="patient-management-name-link"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            handlePatientRowClick(row);
          }}
        >
          <strong>{row.patientName}</strong>
        </a>
      )
    },
    {
      key: 'dob',
      title: 'DOB',
      sortable: true,
      render: (row) => (row.dob ? formatDate(row.dob) : 'N/A')
    },
    {
      key: 'clinicalMinutes',
      title: 'Clinical Min',
      sortable: true,
      className: 'reusable-table-centered-col',
      render: (row) => String(row.clinicalMinutes ?? 0)
    },
    {
      key: 'providerMinutes',
      title: 'Provider Min',
      sortable: true,
      className: 'reusable-table-centered-col',
      render: (row) => String(row.providerMinutes ?? 0)
    },
    {
      key: 'isComplex',
      title: 'Complexity',
      sortable: true,
      render: (row) => (row.isComplex ? 'Complex' : 'Non-Complex')
    },
    {
      key: 'billingPath',
      title: 'Billing Path',
      sortable: true,
      render: (row) => row.billingPath || '—'
    },
    {
      key: 'caregiverDisplay',
      title: 'Caregiver',
      sortable: true,
      render: (row) => row.caregiverDisplay || 'Caregiver N/A'
    },
    {
      key: 'providerDisplay',
      title: 'Provider',
      sortable: true,
      render: (row) => row.providerDisplay || 'Provider N/A'
    },
    {
      key: 'eligibleCptCodes',
      title: 'Eligible CPT Codes',
      sortable: true,
      render: (row) => (
        <span title={row.billingBlocked ? 'Billing blocked — resolve conflicts' : ''}>
          {row.eligibleCptCodes || 'N/A'}
        </span>
      )
    }
  ], [currentPage, pageSize, handlePatientRowClick]);

  const pcmBillingTableColumns = useMemo(() => [
    {
      key: 'rowNo',
      title: '#',
      className: 'reusable-table-centered-col',
      render: (_row, rowIndex) => (currentPage - 1) * pageSize + rowIndex + 1
    },
    {
      key: 'status',
      title: 'Status',
      sortable: true,
      className: 'reusable-table-centered-col',
      render: (row) => <StatusBadge status={row.status || 'N/A'} />
    },
    {
      key: 'patientName',
      title: 'Patient Name',
      sortable: true,
      render: (row) => (
        <a
          href="#"
          className="patient-management-name-link"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            handlePatientRowClick(row);
          }}
        >
          <strong>{row.patientName}</strong>
        </a>
      )
    },
    {
      key: 'dob',
      title: 'DOB',
      sortable: true,
      render: (row) => (row.dob ? formatDate(row.dob) : 'N/A')
    },
    {
      key: 'clinicalMinutes',
      title: 'Clinical Min',
      sortable: true,
      className: 'reusable-table-centered-col',
      render: (row) => String(row.clinicalMinutes ?? 0)
    },
    {
      key: 'providerMinutes',
      title: 'Provider Min',
      sortable: true,
      className: 'reusable-table-centered-col',
      render: (row) => String(row.providerMinutes ?? 0)
    },
    {
      key: 'serviceTime',
      title: 'Service Time',
      sortable: true,
      className: 'reusable-table-centered-col',
      render: (row) => String(row.serviceTime ?? 0)
    },
    {
      key: 'caregiverDisplay',
      title: 'Caregiver',
      sortable: true,
      render: (row) => row.caregiverDisplay || 'Caregiver N/A'
    },
    {
      key: 'providerDisplay',
      title: 'Provider',
      sortable: true,
      render: (row) => row.providerDisplay || 'Provider N/A'
    },
    {
      key: 'eligibleCptCodes',
      title: 'Eligible CPT Codes',
      sortable: true,
      render: (row) => row.eligibleCptCodes || 'N/A'
    }
  ], [currentPage, pageSize, handlePatientRowClick]);

  const billingTableColumns =
    billingProgram === 'pcm'
      ? pcmBillingTableColumns
      : billingProgram === 'ccm'
        ? ccmBillingTableColumns
        : rpmBillingTableColumns;

  const availableEmailColumns = useMemo(
    () => (billingProgram === 'pcm' ? PCM_EMAIL_COLUMNS : billingProgram === 'ccm' ? CCM_EMAIL_COLUMNS : RPM_EMAIL_COLUMNS),
    [billingProgram]
  );

  const selectedCptCodes = Array.isArray(filters.cptCodes) ? filters.cptCodes : [];
  const allCptCodes = dropdownOptions.cptCodes || [];
  const allCptSelected =
    allCptCodes.length > 0 && allCptCodes.every((code) => selectedCptCodes.includes(code));
  const cptTriggerLabel = selectedCptCodes.length === 0
    ? '-- Select --'
    : allCptSelected
      ? 'All'
      : selectedCptCodes.join(', ');

  const enrolledProgramTabs = useMemo(() => {
    const programs = normalizePracticePrograms(practice?.care_programs);
    return MANAGE_BILLING_PROGRAM_TABS.filter((tab) => programs.includes(tab.id));
  }, [practice?.care_programs]);

  useEffect(() => {
    if (!practiceId || !practice?.care_programs) return;
    const programs = normalizePracticePrograms(practice.care_programs);
    const allowed = MANAGE_BILLING_PROGRAM_TABS
      .filter((tab) => programs.includes(tab.id))
      .map((tab) => tab.param);
    if (allowed.length === 0) return;
    if (!allowed.includes(billingProgram)) {
      navigate(`/billing/manage/${practiceId}?program=${allowed[0]}`, {
        replace: true,
        state: location.state
      });
    }
  }, [practice?.care_programs, billingProgram, practiceId, navigate, location.state]);

  const handleProgramTabChange = (tab) => {
    if (tab.param === billingProgram) return;
    includeOptionsNextFetchRef.current = true;
    setCurrentPage(1);
    navigate(`/billing/manage/${practiceId}?program=${tab.param}`, {
      replace: true,
      state: location.state
    });
  };

  return (
    <div className="manage-billing-report-wrapper">
      <header className="manage-billing-header">
        <Heading title={practice?.practice_name || 'Billing Report'} level="h2" className="manage-billing-title" />
        <div className="manage-billing-header-actions">
          <div className="manage-billing-period">
            {activeProgramTab} Billing Period: <span>{getBillingPeriod()}</span>
          </div>
        </div>
      </header>

      <div className="mbr-report-card">
        {enrolledProgramTabs.length > 1 && (
          <div className="ppa-analytics-header mbr-program-tabs-header">
            <div className="ppa-analytics-tabs" role="tablist" aria-label="Billing program">
              {enrolledProgramTabs.map((tab) => (
                <button
                  key={tab.id}
                  type="button"
                  role="tab"
                  aria-selected={activeProgramTab === tab.id}
                  className={`ppa-pill-btn ${activeProgramTab === tab.id ? 'active' : ''}`}
                  onClick={() => handleProgramTabChange(tab)}
                >
                  <FontAwesomeIcon icon={tab.icon} className="ppa-tab-icon" />
                  {tab.label}
                </button>
              ))}
            </div>
          </div>
        )}

        <div className={`mbr-report-panel ${enrolledProgramTabs.length <= 1 ? 'mbr-report-panel--no-tabs' : ''}`}>
        {/* Filter Form Card */}
        <div className="manage-billing-form-card">
          <div className="card-head">{activeProgramTab} Report</div>
          <form
            className="manage-billing-form"
            onSubmit={(e) => {
              e.preventDefault();
              handleQuery();
            }}
          >
            <div className="filter-grid">

              {/* Row 1 */}
              <div className="form-field">
                <label>CPT Code</label>
                <div className="mbr-multiselect" ref={cptDropdownRef}>
                  <button
                    type="button"
                    className="mbr-multiselect-trigger"
                    onClick={() => setIsCptDropdownOpen((prev) => !prev)}
                  >
                    <span>{cptTriggerLabel}</span>
                    <span className="mbr-multiselect-arrow">{isCptDropdownOpen ? '▲' : '▼'}</span>
                  </button>
                  {isCptDropdownOpen && (
                    <div className="mbr-multiselect-menu">
                      <button
                        type="button"
                        className={`mbr-multiselect-option${allCptSelected ? ' selected' : ''}`}
                        onClick={toggleCptAll}
                      >
                        All
                      </button>
                      {allCptCodes.map((code) => (
                        <button
                          key={code}
                          type="button"
                          className={`mbr-multiselect-option${
                            selectedCptCodes.includes(code) ? ' selected' : ''
                          }`}
                          onClick={() => toggleCptCode(code)}
                        >
                          {code}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              <div className="form-field">
                <label>Patient Name</label>
                <select
                  className="form-select-custom"
                  value={filters.patientName}
                  onChange={(e) => handleFilterChange('patientName', e.target.value)}
                >
                  <option value="">-- Select --</option>
                  {dropdownOptions.patients.map(patient => (
                    <option key={patient.id} value={patient.id}>
                      {patient.first_name} {patient.last_name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-field">
                <label>Insurance</label>
                <input
                  type="text"
                  className="form-input-custom"
                  value={filters.insurance}
                  onChange={(e) => handleFilterChange('insurance', e.target.value)}
                  placeholder="Enter insurance"
                />
              </div>

              <div className="form-field">
                <label>Service Time</label>
                <input
                  type="text"
                  className="form-input-custom"
                  value={filters.serviceTime}
                  onChange={(e) => handleFilterChange('serviceTime', e.target.value)}
                  placeholder="Enter service time"
                />
              </div>

              <div className="form-field">
                <label>Status</label>
                <select
                  className="form-select-custom"
                  value={filters.status}
                  onChange={(e) => handleFilterChange('status', e.target.value)}
                >
                  <option value="">-- Select --</option>
                  {dropdownOptions.statuses.map(status => (
                    <option key={status} value={status}>{status}</option>
                  ))}
                </select>
              </div>

              <div className="form-field">
                <label>{billingProgram === 'pcm' ? 'PCM Service Date Range' : billingProgram === 'ccm' ? 'CCM Service Date Range' : 'RPM Service Date Range'}</label>
                <select
                  className="form-select-custom"
                  value={serviceDateMode}
                  onChange={(e) => handleServiceDateModeChange(e.target.value)}
                  aria-label="Choose date range or billing cycle"
                >
                  <option value="">-- Select --</option>
                  <option value="custom">Enter date range</option>
                  <option value="cycle">Select billing cycle</option>
                </select>
              </div>

              {serviceDateMode === 'cycle' && (
                <div className="form-field">
                  <label>Billing Cycle</label>
                  <select
                    className="form-select-custom"
                    value={selectedBillingCycleKey}
                    onChange={(e) => handleBillingCycleSelect(e.target.value)}
                    aria-label="Billing cycle"
                  >
                    <option value="">-- Select billing cycle --</option>
                    {billingCycles.map((cycle) => (
                      <option key={cycle.key} value={cycle.key}>
                        {formatBillingCycleLabel(cycle)}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {serviceDateMode === 'custom' && (
                <div className="form-field mbr-service-date-dates">
                  <label>Date Range</label>
                  <div className="date-range-inputs mbr-rpm-date-range">
                    <div className="mbr-date-field">
                      <AppDateInput
                        ref={rpmStartInputRef}
                        className="form-input-custom mbr-date-input"
                        value={filters.rpmStartDate}
                        max={getTodayYmd()}
                        onChange={handleServiceStartDateChange}
                      />
                      <button
                        type="button"
                        className="mbr-date-calendar-btn"
                        aria-label="Open start date calendar"
                        onClick={() => openDatePicker(rpmStartInputRef)}
                      >
                        <FontAwesomeIcon icon={faCalendarAlt} />
                      </button>
                    </div>
                    <span className="date-sep">~</span>
                    <div className="mbr-date-field">
                      <AppDateInput
                        ref={rpmEndInputRef}
                        className="form-input-custom mbr-date-input"
                        value={filters.rpmEndDate}
                        min={filters.rpmStartDate || undefined}
                        max={getTodayYmd()}
                        onChange={handleServiceEndDateChange}
                      />
                      <button
                        type="button"
                        className="mbr-date-calendar-btn"
                        aria-label="Open end date calendar"
                        onClick={() => openDatePicker(rpmEndInputRef)}
                      >
                        <FontAwesomeIcon icon={faCalendarAlt} />
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* Row 2 */}
              {billingProgram === 'rpm' && (
              <div className="form-field">
                <label>Model #</label>
                <select
                  className="form-select-custom"
                  value={filters.model}
                  onChange={(e) => handleFilterChange('model', e.target.value)}
                >
                  <option value="">-- Select --</option>
                  {dropdownOptions.models.map(model => (
                    <option key={model} value={model}>{model}</option>
                  ))}
                </select>
              </div>
              )}

              {billingProgram === 'rpm' && (
              <div className="form-field">
                <label>Serial #</label>
                <input
                  type="text"
                  className="form-input-custom"
                  value={filters.serial}
                  onChange={(e) => handleFilterChange('serial', e.target.value)}
                  placeholder="Enter serial number"
                />
              </div>
              )}

              {billingProgram === 'rpm' && (
              <div className="form-field">
                <label>Measurement Days</label>
                <select
                  className="form-select-custom"
                  value={filters.measurementDays}
                  onChange={(e) => handleFilterChange('measurementDays', e.target.value)}
                >
                  <option value="">-- Select --</option>
                  {dropdownOptions.measurementDays.map(days => (
                    <option key={days} value={days}>{days}</option>
                  ))}
                </select>
              </div>
              )}

              {/* Row 3 */}
              <div className="form-field">
                <label>Caregiver</label>
                <select
                  className="form-select-custom"
                  value={filters.caregiver}
                  onChange={(e) => handleFilterChange('caregiver', e.target.value)}
                >
                  <option value="">-- Select --</option>
                  {dropdownOptions.caregivers.map(caregiver => (
                    <option key={caregiver.id} value={caregiver.id}>
                      {caregiver.first_name} {caregiver.last_name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-field">
                <label>Provider</label>
                <select
                  className="form-select-custom"
                  value={filters.provider}
                  onChange={(e) => handleFilterChange('provider', e.target.value)}
                >
                  <option value="">-- Select --</option>
                  {dropdownOptions.providers.map(provider => (
                    <option key={provider.id} value={provider.id}>
                      {provider.first_name} {provider.last_name}
                    </option>
                  ))}
                </select>
              </div>

            </div>

            {/* Action Buttons */}
            <div className="form-actions">
              <div className="spacer" />
              <div className="actions-right">
                <button type="button" className="reset-btn" onClick={handleReset}>
                  Reset
                </button>
                <button type="submit" className="query-btn">
                  Query
                </button>
                <button
                  type="button"
                  className="generate-billing-btn"
                  onClick={() => setShowGenerateBilling(true)}
                >
                  Generate Billing
                </button>
                <button type="button" className="print-btn" onClick={handlePrint}>
                  Print
                </button>
                <div className="export-dropdown">
                  <button
                    type="button"
                    className="export-btn"
                    onClick={() => setShowExportMenu(!showExportMenu)}
                  >
                    Export <span className="caret">▾</span>
                  </button>
                  <div className={`export-menu ${showExportMenu ? 'show' : ''}`}>
                    <button
                      type="button"
                      onClick={() => {
                        handleExport('csv');
                        setShowExportMenu(false);
                      }}
                    >
                      Export to CSV
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        handleExport('pdf');
                        setShowExportMenu(false);
                      }}
                    >
                      Export to PDF
                    </button>
                  </div>
                </div>
                <button type="button" className="email-btn" onClick={handleEmail}>
                  Email
                </button>
              </div>
            </div>
          </form>
        </div>

        {/* Data Table */}
        <div className="manage-billing-table-section">
          {/* Top Toolbar: Search Only (Top Right) */}
          <div className="billing-table-toolbar">
            <div className="spacer"></div>

            {/* Search Bar Top Right */}
            <SearchBar value={searchTerm} onChange={setSearchTerm} className="search-block" />
          </div>

          <div className="practice-tab-table-card">
            <Table
              columns={billingTableColumns}
              data={filteredPatients}
              sortBy={sortBy}
              sortOrder={sortOrder}
              onSort={handleSort}
              onRowClick={handlePatientRowClick}
              isLoading={tableLoading}
              loadingMode="skeleton"
              skeletonRows={10}
              getRowKey={(row, rowIndex) =>
                row?.id != null ? `billing-${row.id}-${rowIndex}` : `billing-row-${rowIndex}`
              }
              emptyMessage={
                tableLoading || !hasLoadedOnce
                  ? ''
                  : 'No billing data found'
              }
            />

            {(pagination.total > 0 || filteredPatients.length > 0) ? (
              <div className="scm-list-footer">
                <ListScreenPaginationBar
                  entriesValue={entriesPerPage === 'all' ? 'all' : String(entriesPerPage)}
                  onEntriesChange={(value) => {
                    setEntriesPerPage(value === 'all' ? 'all' : value);
                    setCurrentPage(1);
                  }}
                  currentPage={pagination.current_page || currentPage}
                  totalPages={totalPages}
                  onPageChange={handlePageChange}
                />
              </div>
            ) : null}
          </div>

        </div>
        </div>
        {(pagination.total > 0 || filteredPatients.length > 0) && (
          <div className="mbr-event-log-actions">
            <button
              type="button"
              className="scm-events-log-btn"
              onClick={() => setShowLogSection(true)}
            >
              <FontAwesomeIcon icon={faList} className="list-screen-me-2" />
              Event Logs
            </button>
          </div>
        )}
      </div>

      <GenerateBillingModal
        isOpen={showGenerateBilling}
        onClose={() => setShowGenerateBilling(false)}
        practiceId={practiceId}
        practiceName={practice?.practice_name || 'Billing Report'}
        program={billingProgram}
        periodLabel={getBillingPeriod()}
        periodStart={getBillingPeriodBounds().start}
        periodEnd={getBillingPeriodBounds().end}
        cptCatalog={getBillingCptCodesForProgram(billingProgram)}
        loadPatients={() => fetchAllBillingPatients({
          ...buildDefaultFilters(),
          rpmStartDate: (filtersRef.current || filters).rpmStartDate || '',
          rpmEndDate: (filtersRef.current || filters).rpmEndDate || ''
        }, { ignoreSearch: true })}
      />

      <EmailModal
        isOpen={showEmailModal}
        onClose={() => setShowEmailModal(false)}
        defaultSubject={defaultEmailSubject}
        defaultContent={defaultEmailContent}
        availableColumns={availableEmailColumns}
        onSend={handleSendEmail}
        onPreview={handlePreviewEmailReport}
      />

      <BillingReportPreviewModal
        isOpen={showReportPreviewModal}
        onClose={() => setShowReportPreviewModal(false)}
        reportData={reportPreviewData}
        onDownloadCsv={handleDownloadPreviewCsv}
        onProceedToSend={showEmailModal ? () => setShowReportPreviewModal(false) : undefined}
      />

      <PrintModal
        isOpen={showPrintModal}
        onClose={() => setShowPrintModal(false)}
        availableColumns={availableEmailColumns}
        onPrint={handlePerformPrint}
        title="Select Data Fields"
      />

      {showLogSection && (
        <div
          ref={logOverlayRef}
          className="practices-list-modal-overlay"
          role="presentation"
        >
          <div
            ref={logContainerRef}
            className="practices-list-modal-container"
            style={{ maxWidth: '900px', width: '90%' }}
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-labelledby="manage-billing-event-logs-title"
          >
            <div className="practices-list-modal-header">
              <h5 id="manage-billing-event-logs-title" className="practices-list-modal-title">
                Billing Event Logs
              </h5>
              <button
                type="button"
                className="practices-list-modal-close"
                onClick={() => setShowLogSection(false)}
                aria-label="Close"
              >
                ×
              </button>
            </div>
            <div className="practices-list-modal-body" style={{ maxHeight: '70vh', overflowY: 'auto' }}>
              <EventLogSection moduleId="Billing" practiceId={practiceId} />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ManageBillingReport;
